import type { ReploClipboard } from "@v1/utils/copyPaste";

import * as React from "react";

import {
  selectComponentDataMapping,
  selectComponentMapping,
  selectDraftComponentIds,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  selectGetAttribute,
} from "@v1/reducers/core-reducer";
import { selectExplicitExpandedNodeIds } from "@v1/reducers/tree-reducer";
import { useEditorStore } from "@v1/store/store";
import {
  canMoveComponentToParent,
  getParentComponentFromMapping,
} from "@v1/utils/component";
import { getChildren } from "replo-runtime/shared/utils/component";
import { getFromRecordOrNull } from "replo-runtime/shared/utils/optional";

const contextMenuSource = "contextMenu";

export const useFindParentForPaste = () => {
  const store = useEditorStore();

  const findParentForPaste = React.useCallback(
    (reploClipboard: ReploClipboard, componentId: string) => {
      const storeState = store.getState();
      const draftElement =
        selectDraftElement_warningThisWillRerenderOnEveryUpdate(storeState);
      const componentMapping = selectComponentMapping(storeState);
      const explicitExpandedNodeIds = selectExplicitExpandedNodeIds(storeState);
      const draftComponentIds = selectDraftComponentIds(storeState);
      const isMultiSelect = draftComponentIds.length > 1;
      const componentSelectedForPaste = getFromRecordOrNull(
        componentMapping,
        componentId,
      )?.component;
      let newParent = componentSelectedForPaste!;
      let positionWithinSiblings = 0;

      const component = (() => {
        if (reploClipboard.type === "figmaPluginExportv2") {
          return reploClipboard.reploComponent;
        }

        // TODO (Evan, 2024-07-25): Clean this up more, why
        // is this so heinous
        return (
          reploClipboard.type !== "copyPasteStyles" &&
          reploClipboard.type !== "pasteCSSStyles" &&
          (reploClipboard.type === "singleComponent" ||
          reploClipboard.type === "text" ||
          reploClipboard.type === "image"
            ? reploClipboard.component
            : reploClipboard.components[0]!)
        );
      })();

      if (!component || !draftElement) {
        return null;
      }

      const parentOfSelectedComponent =
        componentSelectedForPaste &&
        getParentComponentFromMapping(
          componentMapping,
          componentSelectedForPaste.id,
        );

      const newSingleComponentId =
        reploClipboard.type === "singleComponent" &&
        reploClipboard.component?.id;

      if (parentOfSelectedComponent) {
        const canMoveInside = canMoveComponentToParent(
          draftElement,
          component,
          componentSelectedForPaste,
          selectGetAttribute(storeState),
          contextMenuSource,
          selectComponentDataMapping(storeState),
        ).canMove;
        const isEmptyContainer =
          componentSelectedForPaste.type === "container" &&
          getChildren(componentSelectedForPaste).length === 0;

        // Note (Noah, 2022-02-07): If we can't move component as a child or pasting in the same component, paste
        // as a sibling instead
        if (
          componentSelectedForPaste &&
          (!canMoveInside ||
            newSingleComponentId === componentId ||
            isMultiSelect ||
            // NOTE (Fran 2024-04-16): If the parent is a container and it's empty, we should paste the component inside it.
            (!isEmptyContainer &&
              !explicitExpandedNodeIds.includes(componentId)))
        ) {
          newParent = parentOfSelectedComponent!;
          positionWithinSiblings =
            getChildren(newParent!).indexOf(componentSelectedForPaste) + 1;
        }
      }

      return {
        newParent,
        positionWithinSiblings,
      };
    },
    [store],
  );

  return findParentForPaste;
};
